import { Moon, Sun, Monitor, Palette, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/hooks/use-theme';

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return (
          <div className="relative">
            <Sun className="h-4 w-4 text-yellow-500" />
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
          </div>
        );
      case 'dark':
        return (
          <div className="relative">
            <Moon className="h-4 w-4 text-blue-400" />
            <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-blue-300 rounded-full animate-pulse" />
          </div>
        );
      case 'system':
        return (
          <div className="relative">
            <Monitor className="h-4 w-4 text-slate-500" />
            <Zap className="absolute -top-1 -right-1 w-2 h-2 text-slate-400" />
          </div>
        );
      default:
        return (
          <div className="relative">
            <Palette className="h-4 w-4 text-emerald-500" />
            <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
          </div>
        );
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 w-9 px-0 transition-all duration-300 hover:border hover:border-[var(--border-color)] group"
          title={`Current theme: ${theme}`}
        >
          <div className="transition-transform duration-300 group-hover:scale-110">
            {getThemeIcon()}
          </div>
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 p-2">
        <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground mb-2 px-2">
          🎨 Theme Settings
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="mb-1" />

        <DropdownMenuItem
          onClick={() => setTheme('light')}
          className={`group cursor-pointer rounded-lg p-3 transition-all duration-200 ${theme === 'light'
            ? 'border'
            : 'hover:border'
            }`}
        >
          <div className="flex items-center w-full">
            <div className={`p-2 rounded-lg mr-3 transition-all duration-200 ${theme === 'light'
              ? 'bg-yellow-100 dark:bg-yellow-900/50 shadow-sm'
              : 'bg-yellow-50 dark:bg-yellow-950/30 group-hover:bg-yellow-100 dark:group-hover:bg-yellow-900/40'
              }`}>
              <Sun className={`h-4 w-4 transition-colors duration-200 ${theme === 'light' ? 'text-yellow-600 dark:text-yellow-400' : 'text-yellow-500'
                }`} />
            </div>
            <div className="flex-1">
              <div className={`font-medium transition-colors duration-200 ${theme === 'light' ? 'text-yellow-800 dark:text-yellow-200' : 'text-foreground'
                }`}>
                Light Mode
              </div>
              <div className="text-xs text-muted-foreground">
                Bright and clean interface
              </div>
            </div>
            {theme === 'light' && (
              <div className="ml-2 w-2 h-2 rounded-full" />
            )}
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => setTheme('dark')}
          className={`group cursor-pointer rounded-lg p-3 transition-all duration-200 ${theme === 'dark'
            ? 'border'
            : 'hover:border'
            }`}
        >
          <div className="flex items-center w-full">
            <div className={`p-2 rounded-lg mr-3 transition-all duration-200 ${theme === 'dark'
              ? 'bg-blue-900/50 shadow-sm'
              : 'bg-blue-50 group-hover:bg-blue-100'
              }`}>
              <Moon className={`h-4 w-4 transition-colors duration-200 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-500'
                }`} />
            </div>
            <div className="flex-1">
              <div className={`font-medium transition-colors duration-200 ${theme === 'dark' ? 'text-blue-200' : 'text-foreground'
                }`}>
                Dark Mode
              </div>
              <div className="text-xs text-muted-foreground">
                Easy on the eyes
              </div>
            </div>
            {theme === 'dark' && (
              <div className="ml-2 w-2 h-2 rounded-full" />
            )}
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => setTheme('system')}
          className={`group cursor-pointer rounded-lg p-3 transition-all duration-200 ${theme === 'system'
            ? 'border'
            : 'hover:border'
            }`}
        >
          <div className="flex items-center w-full">
            <div className={`p-2 rounded-lg mr-3 transition-all duration-200 ${theme === 'system'
                ? 'bg-slate-100 dark:bg-slate-800/50 shadow-sm'
                : 'bg-slate-50 dark:bg-slate-900/30 group-hover:bg-slate-100 dark:group-hover:bg-slate-800/40'
              }`}>
              <div className="relative">
                <Monitor className={`h-4 w-4 transition-colors duration-200 ${theme === 'system' ? 'text-slate-600 dark:text-slate-400' : 'text-slate-500'
                  }`} />
                <Zap className={`absolute -top-1 -right-1 w-2.5 h-2.5 transition-colors duration-200 ${theme === 'system' ? 'text-slate-400 dark:text-slate-300' : 'text-slate-400'
                  }`} />
              </div>
            </div>
            <div className="flex-1">
              <div className={`font-medium transition-colors duration-200 ${theme === 'system' ? 'text-slate-800 dark:text-slate-200' : 'text-foreground'
                }`}>
                System
              </div>
              <div className="text-xs text-muted-foreground">
                Follows your device settings
              </div>
            </div>
            {theme === 'system' && (
              <div className="ml-2 w-2 h-2 rounded-full" />
            )}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
